import React, { useEffect, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Form, Formik } from "formik";
import { Button } from "@thrivetribe/gloji.ui.atoms.button";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import {
  ModalPageHeader,
  ModalPageBody,
  ModalPageFooter,
  ModalPageWrapper,
  ModalPageHeading,
} from "../../../organisms/ModalPage";
import { LoadingPage } from "../LoadingPage";
import { update_signup_form_values } from "../../../../store/actions/signupFlowData";
import { EthnicityValidationSchema } from "../../../../data/Validation/EligibilityCheckerValidation";
import "./Ethnicity.scss";
import { ethnicityOptions } from "../../../../enums/profile/Ethnicity";
import ReactSelect from "../../../atoms/ReactSelect";
import { ProgrammeProps } from "../../EligibilityCheckerGeneric/Props";
import { PowerOfTen, ProgressStepEnum, setStepProgress } from "../../../../redux/slices/progressBarSlice";

export interface EthnicityProps extends ProgrammeProps {
  ethnicity: string;
  progressPercentage?: PowerOfTen;
}

function Ethnicity({ ethnicity, progressPercentage }: Readonly<EthnicityProps>) {
  const dispatch = useDispatch();
  const ethnicityPageData = useSelector((state: any) => state.signupFlowData.signupPagesData?.ethnicity);
  const storeEthnicityValue = useSelector((state: any) => state.signupFlowData.signupFormValues?.ethnicity);
  const [showLoadingPage, setShowLoadingPage] = useState(false);
  const history = useHistory();

  const profileEthnicityObject = ethnicity
    ? {
        label: ethnicityOptions.find(option => option.value.toLowerCase() === ethnicity.toLowerCase())?.label,
        value: ethnicity,
      }
    : null;
  const initialEthnicity = storeEthnicityValue || profileEthnicityObject || null;

  useEffect(() => {
    if (progressPercentage) {
      dispatch(
        setStepProgress({
          progressStepEnum: ProgressStepEnum.CheckEligiblity,
          progressPercentage: progressPercentage,
        })
      );
    }
  }, [dispatch, progressPercentage]);

  useEffect(() => {
    window.gtag("event", "page_view", {
      page_title: "Ethnicity | Eligibility checker | Sign up | Gloji",
    });
  }, []);

  const handleSubmit = values => {
    dispatch(update_signup_form_values(values));
    setShowLoadingPage(true);
  };

  return (
    <>
      {showLoadingPage === true ? (
        <LoadingPage
          callback={() => {
            setShowLoadingPage(false);
            history.push("/bmi");
          }}
          heading={ethnicityPageData?.loadingScreen.title ?? ""}
          text={ethnicityPageData?.loadingScreen.body ?? ""}
        />
      ) : (
        <Formik
          initialValues={{ ethnicity: initialEthnicity }}
          onSubmit={handleSubmit}
          validationSchema={EthnicityValidationSchema}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {props => (
            <Form className="eligibility-checker-form ethnicity-form">
              <Helmet>
                <title>Ethnicity | Eligibility checker | Sign up | Gloji</title>
              </Helmet>
              <ModalPageWrapper>
                <ModalPageBody className="with-sticky-footer">
                  <ModalPageHeader
                    backButtonOnClick={() => {
                      history.goBack();
                    }}
                  >
                    Checking we're a good fit
                  </ModalPageHeader>
                  <ModalPageHeading>{ethnicityPageData?.questions[0].question}</ModalPageHeading>
                  <ReactSelect
                    name="ethnicity"
                    options={ethnicityOptions}
                    label={ethnicityPageData?.questions[0].label}
                    labelCssClass="eligibility-checker__input-label"
                    value={ethnicityOptions.find(option => option.value === initialEthnicity)}
                  />
                  <Accordion square={true} elevation={0} className="eligibility-checker__accrodion">
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon fontSize="large" />}
                      aria-controls={`ethnicity-question-explanation`}
                    >
                      <span>{ethnicityPageData?.questions[0].whyQuestion}</span>
                    </AccordionSummary>
                    <AccordionDetails>
                      <span>{ethnicityPageData?.questions[0].whyQuestionDetails}</span>
                    </AccordionDetails>
                  </Accordion>
                </ModalPageBody>
                <ModalPageFooter className="sticky-footer">
                  <Button type="submit" variant="contained" color="primary">
                    Continue
                  </Button>
                </ModalPageFooter>
              </ModalPageWrapper>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
}

export default Ethnicity;
